import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetDashboardHeader,
  setDashboardHeaderWidths,
  setDashboardPagination,
} from '../redux/actions/actions';
import { IHeader, IHeaderColumn, IStore, UserRoleEnums } from '../redux/store/IStore';
import {
  UserHeader,
  AdminHeader,
  Attributes,
  genereateReduxHeader,
} from '../components/Dashboard/IDashboard';
import _ from 'lodash';

export interface IUserLocalSettings {
  pageSize?: number;
}

// Simple interface for summary settings
interface ISummarySettings {
  model: string;
  promptId: string;
}

export default function useLocalStorage() {
  const user = useSelector((state: IStore) => state.user);
  const dispatch = useDispatch();

  // 1. Function to set Redux header
  const setReduxHeader = useCallback(
    (header: IHeader) => {
      const toReduxWidths = {},
        toReduxHeader = {};

      const entries = Object.entries(header) as [Attributes, IHeaderColumn][];

      entries.forEach(([key, item]) => {
        toReduxWidths[key] = item.width;

        toReduxHeader[key] = {
          ...item,
          filtered: false,
        };
      });

      dispatch(setDashboardHeaderWidths(toReduxWidths));
      dispatch(resetDashboardHeader(toReduxHeader));
    },
    [dispatch]
  );

  // 2. Function to validate dashboard header
  const validateDashboardHeader = useCallback(
    (isAdmin: boolean) => {
      if (!user) return false;

      const storageBlob = localStorage.getItem(`editor-${user.username}-header`);
      if (!storageBlob) return false;

      const localStorageHeader = JSON.parse(storageBlob) as IHeader;
      const header = isAdmin ? AdminHeader : UserHeader;
      const keys = Object.keys(localStorageHeader);
      const currentKeys = Object.keys(header);

      if (!_.isEqual(currentKeys, keys)) return false;

      setReduxHeader(localStorageHeader); // This is the dependency causing the warning
      return true;
    },
    [user, setReduxHeader] // Include setReduxHeader as a dependency
  );

  // 3. Fetch summary settings
  const getSummarySettings = useCallback((): ISummarySettings | null => {
    if (!user) return null;

    const storageBlob = localStorage.getItem(`editor-${user.username}-summarize-v2-settings`);
    if (!storageBlob) return null;

    try {
      return JSON.parse(storageBlob) as ISummarySettings;
    } catch {
      console.warn(`Failed to parse summary settings for user: ${user.username}`);
      return null;
    }
  }, [user]);

  // 4. Fetch generic local storage data
  const getItem = useCallback(
    (key: string): any | null => {
      if (!user) return null;

      const storageBlob = localStorage.getItem(`editor-${user.username}-${key}`);
      if (!storageBlob) return null;

      try {
        return JSON.parse(storageBlob);
      } catch {
        console.warn(`Failed to parse local storage item for key: ${key}`);
        return null;
      }
    },
    [user]
  );

  // 5. Effect to set Redux state from local storage settings
  useEffect(() => {
    if (!user) return;

    const isAdmin = user.userRoles.includes(UserRoleEnums.ADMIN);

    if (!validateDashboardHeader(isAdmin)) {
      setReduxHeader(genereateReduxHeader(isAdmin ? AdminHeader : UserHeader));
    }

    const userSettings = localStorage.getItem(`editor-${user.username}-page-size`);
    if (!userSettings) return;

    const { pageSize } = JSON.parse(userSettings) as IUserLocalSettings;
    if (pageSize) {
      dispatch(setDashboardPagination({ pageSize: pageSize }));
    }
  }, [user, dispatch, validateDashboardHeader, setReduxHeader]);

  return {
    getSummarySettings, // For fetching ISummarySettings
    getItem, // For generic key-value access
  };
}
